import { supabase } from '../supabaseClient';
import type { BaseTimetableByTerm, GradeProgramColors } from './sectionLayoutService';
import { normalizeBaseTimetableByTerm, normalizeGradeProgramColors } from './sectionLayoutService';

export interface TeamLeaderProgramColorPreview {
  leaderWorkspaceId: string;
  regular: string[];
  dedicated: string[];
  baseExclude: string[];
  project: string[];
}

export interface TeamLeaderTimetableSettingsEntryPreview {
  grade_id: string;
  grade_name: string;
  colors: GradeProgramColors;
  /** 팀장 기초시간표 (1·2학기 구분 형식) */
  grid: BaseTimetableByTerm;
}

export interface TeamLeaderTimetableSettingsPreviewPayload {
  leaderWorkspaceId: string;
  entries: TeamLeaderTimetableSettingsEntryPreview[];
}

function parseHexColors(raw: unknown): string[] {
  if (!Array.isArray(raw)) return [];
  return raw.filter((v): v is string => typeof v === 'string' && /^#[0-9A-Fa-f]{6}$/.test(v));
}

export async function getTeamLeaderProgramColorSettingsPreview(
  teamId: string
): Promise<TeamLeaderProgramColorPreview> {
  const { data, error } = await supabase.rpc('get_team_leader_program_color_settings_preview', {
    p_team_id: teamId,
  });
  if (error) throw new Error(error.message);
  const raw = data as Record<string, unknown> | null;
  if (!raw || typeof raw !== 'object') {
    return { leaderWorkspaceId: '', regular: [], dedicated: [], baseExclude: [], project: [] };
  }
  const r = raw as Record<string, unknown>;
  const regular = parseHexColors(r['regular']);
  return {
    leaderWorkspaceId: String(r['leader_workspace_id'] ?? ''),
    regular: regular.length > 0 ? regular : parseHexColors(['#4e8d72']),
    dedicated: parseHexColors(r['dedicated']),
    baseExclude: parseHexColors(r['baseExclude']),
    project: parseHexColors(r['project']),
  };
}

function parseTimetableSettingsEntry(raw: unknown): TeamLeaderTimetableSettingsEntryPreview | null {
  if (!raw || typeof raw !== 'object') return null;
  const o = raw as Record<string, unknown>;
  const gradeId = String(o['grade_id'] ?? '').trim();
  if (!gradeId) return null;
  const gradeName = String(o['grade_name'] ?? gradeId).trim() || gradeId;
  return {
    grade_id: gradeId,
    grade_name: gradeName,
    colors: normalizeGradeProgramColors(o['colors']),
    grid: normalizeBaseTimetableByTerm(o['grid']),
  };
}

export async function getTeamLeaderTimetableSettingsPreview(
  teamId: string
): Promise<TeamLeaderTimetableSettingsPreviewPayload> {
  const { data, error } = await supabase.rpc('get_team_leader_timetable_settings_preview', {
    p_team_id: teamId,
  });
  if (error) throw new Error(error.message);
  const raw = data as Record<string, unknown> | null;
  if (!raw || typeof raw !== 'object') {
    return { leaderWorkspaceId: '', entries: [] };
  }
  const entriesRaw = raw['entries'];
  const entries = Array.isArray(entriesRaw)
    ? entriesRaw
        .map(parseTimetableSettingsEntry)
        .filter((entry): entry is TeamLeaderTimetableSettingsEntryPreview => entry != null)
    : [];
  return {
    leaderWorkspaceId: String(raw['leader_workspace_id'] ?? ''),
    entries,
  };
}
